import { useIntl, type UserRequirementStateStatus, useSettings } from '@capsicum/common';
import { NCOnboardingRoutesEnum, useNCWOnboardingRoutes } from '@capsicum/features/nc-onboarding/shared';
import { useMemo } from 'react';

import { useVerifyModalRequirements } from './useVerifyModalRequirements';

type VerificationStep = {
	description: string;
	level: 2 | 3;
	status: string;
	title: string;
};

export const useVerifyModal = () => {
	const { $t } = useIntl();
	const { goToPage } = useNCWOnboardingRoutes({});
	const {
		settings: { contactUsHereLink }
	} = useSettings();
	const { isSumsubDoneOrWaiting, poaRequirementStatus, poiRequirementStatus } = useVerifyModalRequirements();

	const getParsedRequirementStatus = (status: null | undefined | UserRequirementStateStatus) => {
		if (!status) return 'notStarted';
		switch (status) {
			case 'completed_allow':
				return 'completed';
			case 'under_review':
			case 'waiting':
				return 'underReview';
			case 'retry':
				return 'rejected';
			default:
				return 'notStarted';
		}
	};

	const level2Status = getParsedRequirementStatus(poiRequirementStatus?.status);
	const level3Status = getParsedRequirementStatus(poaRequirementStatus?.status);

	const getLevelParsedStatus = (level: 2 | 3) => (level === 2 ? level2Status : level3Status);
	const shouldShowStatus = (level: 2 | 3) => getLevelParsedStatus(level) !== 'notStarted';
	const stepsAreCompleted = level2Status === 'completed' && level3Status === 'completed';

	const statusText = (level: 2 | 3) => {
		const status = getLevelParsedStatus(level);

		switch (status) {
			case 'underReview':
				return $t('ncOnboard.verify.status.underReview');
			case 'completed':
				return $t('ncOnboard.verify.status.completed');
			case 'rejected':
				if (level === 3 && level2Status === 'rejected') {
					return $t('ncOnboard.verify.status.rejected');
				}

				return $t('ncOnboard.verify.status.rejectedTryAgain');
			default:
				return '';
		}
	};

	const verificationSteps: Array<VerificationStep> = [
		{
			description: $t('ncOnboard.verify.identity.description'),
			level: 2,
			status: statusText(2),
			title: $t('ncOnboard.verify.identity.title')
		},
		{
			description: $t('ncOnboard.verify.address.description'),
			level: 3,
			status: statusText(3),
			title: $t('ncOnboard.verify.address.title')
		}
	];

	const isStepDisabled = (level: 2 | 3) => {
		const status = getLevelParsedStatus(level);

		return status === 'completed' || status === 'underReview' || (currentStep === 2 && level === 3);
	};

	const userIsBlocked =
		(level2Status === 'completed' && level3Status === 'underReview') ||
		(level2Status === 'underReview' && level3Status === 'completed') ||
		(level2Status === 'underReview' && level3Status === 'underReview');

	const shouldShowDescription = (level: 2 | 3) => !userIsBlocked && level === currentStep;
	const descriptionText = $t(`ncOnboard.verify.description.${userIsBlocked ? 'blocked' : 'default'}`);

	const currentStep = useMemo(() => (isSumsubDoneOrWaiting ? 3 : 2), [isSumsubDoneOrWaiting]);

	const getStepIcon = (level: 2 | 3) => {
		const status = getLevelParsedStatus(level);

		return status === 'notStarted' ? level - 1 : status;
	};

	const buttonText = (() => {
		const currentStatus = currentStep === 2 ? level2Status : level3Status;

		if (currentStatus === 'rejected') return $t('ncOnboard.verify.button.tryAgain');

		return $t(`ncOnboard.verify.button.${currentStep === 2 ? 'verifyIdentity' : 'verifyAddress'}`);
	})();

	const action = (close: () => void) => {
		const route =
			currentStep === 2
				? NCOnboardingRoutesEnum.IDENTITY_VERIFICATION
				: NCOnboardingRoutesEnum.ADDRESS_VERIFICATION;

		goToPage(route);
		close();
	};

	return {
		action,
		buttonText,
		contactUsHereLink,
		descriptionText,
		getStepIcon,
		isStepDisabled,
		shouldShowDescription,
		shouldShowStatus,
		stepsAreCompleted,
		userIsBlocked,
		verificationSteps
	};
};
